import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  FiAlertTriangle,
  FiTruck,
  FiTool,
  FiSearch,
  FiAward,
} from "react-icons/fi";

import { HiOutlineShieldExclamation } from "react-icons/hi2";
import {
  IoBugOutline,
  IoThermometerOutline,
  IoCloseOutline,
} from "react-icons/io5";

import CreateEquipmentFailureModal from "../../incident-log/modals/CreateEquipmentFailureModal";
import CreateFoodSpillModal from "../../incident-log/modals/CreateFoodSpillModal";
import CreateStaffInjuryModal from "../../incident-log/modals/CreateStaffInjuryModal";

const SidebarWidgets = () => {
  const navigate = useNavigate();

  // One state controls all quick-action modals
  const [activeModal, setActiveModal] = useState(null);

  const requirements = [
    {
      title: "Weekly Fire Alarm Test",
      category: "Fire Safety Check",
      badgeText: "Due in 2 days",
      badgeStyle: "bg-orange-50 text-orange-600 border-orange-100",
      icon: <HiOutlineShieldExclamation className="text-red-500 text-sm" />,
      iconBg: "bg-red-50",
    },
    {
      title: "Pest Control Visit",
      category: "Pest Control",
      badgeText: "Due in 6 days",
      badgeStyle: "bg-orange-50 text-orange-600 border-orange-100",
      icon: <IoBugOutline className="text-emerald-600 text-sm" />,
      iconBg: "bg-emerald-50",
    },
    {
      title: "PAT Certificate Renewal",
      category: "Equipment Check",
      badgeText: "Due in 28 days",
      badgeStyle: "bg-indigo-50 text-indigo-600 border-indigo-100",
      icon: <FiAward className="text-purple-600 text-sm" />,
      iconBg: "bg-purple-50",
    },
    {
      title: "Internal Audit",
      category: "Audit & Inspections",
      badgeText: "Due in 35 days",
      badgeStyle: "bg-blue-50 text-blue-600 border-blue-100",
      icon: <FiSearch className="text-blue-600 text-sm" />,
      iconBg: "bg-blue-50",
    },
  ];

  const quickActions = [
    {
      label: "Log Incident",
      icon: <FiAlertTriangle className="text-red-500 text-base" />,
      bg: "bg-red-50",
      action: () => navigate("/incident"),
    },
    {
      label: "Food Spill",
      icon: <FiTruck className="text-emerald-600 text-base" />,
      bg: "bg-emerald-50",
      action: () => setActiveModal("food-spill"),
    },
    {
      label: "Equipment Fault",
      icon: <FiTool className="text-indigo-600 text-base" />,
      bg: "bg-indigo-50",
      action: () => setActiveModal("equipment-failure"),
    },
    {
      label: "Staff Injury",
      icon: <IoThermometerOutline className="text-blue-500 text-base" />,
      bg: "bg-blue-50",
      action: () => setActiveModal("staff-injury"),
    },
  ];

  // Close modal
  const closeModal = () => {
    setActiveModal(null);
  };

  return (
    <>
      <div className="w-full max-w-[360px] flex flex-col gap-4 font-sans">
        {/* 1. Upcoming Requirements Card */}
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-bold text-slate-900">
              Upcoming Requirements
            </h3>

            <button
              type="button"
              onClick={() => navigate("/requirements")}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
            >
              View all
            </button>
          </div>

          <div className="flex flex-col gap-4">
            {requirements.map((req, i) => (
              <div key={i} className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${req.iconBg}`}
                  >
                    {req.icon}
                  </div>

                  <div>
                    <h4 className="text-[11px] font-bold text-slate-800 leading-snug">
                      {req.title}
                    </h4>

                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {req.category}
                    </p>
                  </div>
                </div>

                <span
                  className={`text-[10px] font-medium px-2 py-0.5 rounded border tracking-wide shrink-0 ${req.badgeStyle}`}
                >
                  {req.badgeText}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 2. Quick Actions Card */}
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900 mb-4">
            Quick Actions
          </h3>

          <div className="grid grid-cols-4 gap-2">
            {quickActions.map((action, i) => (
              <button
                key={i}
                type="button"
                onClick={action.action}
                className="flex flex-col items-center group"
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105 shadow-sm border border-black/5 ${action.bg}`}
                >
                  {action.icon}
                </div>

                <span className="text-[9px] font-semibold text-slate-700 mt-2 text-center leading-tight max-w-[64px] block">
                  {action.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* =====================================================
          COMMON FULL SCREEN MODAL PARENT
         ===================================================== */}

      {activeModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          {/* Backdrop */}
          <div className="absolute inset-0" onClick={closeModal} />

          {/* Modal Container */}
          <div className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Optional close button */}
            <button
              type="button"
              onClick={closeModal}
              className="absolute top-3 right-3 z-50 w-8 h-8 flex items-center justify-center rounded-lg bg-white/90 text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors shadow-sm"
            >
              <IoCloseOutline className="text-xl" />
            </button>

            {/* ================================
                Child Modal
               ================================= */}

            {activeModal === "equipment-failure" && (
              <CreateEquipmentFailureModal onClose={closeModal} />
            )}

            {activeModal === "food-spill" && (
              <CreateFoodSpillModal onClose={closeModal} />
            )}

            {activeModal === "staff-injury" && (
              <CreateStaffInjuryModal onClose={closeModal} />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SidebarWidgets;
